import { Koi } from '@ksyos/koi';
import type { ISchemaContainer } from './schemaContainer';
import { EpisodeCategory } from '../enums/episodeCategory';
import { Gender } from '../enums/gender';
import { MaritalStatus } from '../enums/maritalStatus';
import { PatientSearchOptionWithBirthDate } from '../enums/patientSearchOptionWithBirthDate';
import { emailSchema } from './schemas/emailSchema';
import { phoneNumberNoErrorSchema } from './schemas/phoneNumberSchema';

export interface IPatientAddEditValidated {
    birthDate: string;
    careProviderOrganizationId: string | null;
    careProviderPractitionerId: string | null;
    city: string | null;
    country: string | null;
    deceasedDate: string | null;
    emailAddress: string | null;
    gender: Gender;
    homePhoneNumber: string | null;
    houseLetter: string | null;
    houseNumber: string | null;
    houseNumberSuffix: string | null;
    identifierSocialSecurity: string | null;
    isDeceased: boolean;
    maritalStatus: MaritalStatus | null;
    mobileNumber: string | null;
    nameFamily: string;
    nameFamily2: string | null;
    nameGiven: string | null;
    nameInfix: string | null;
    nameInfix2: string | null;
    nameUsual: string | null;
    profession: string | null;
    state: string | null;
    street: string | null;
    zip: string | null;
}

const patientBase = Koi.object().keys({
    birthDate: Koi.koi().date(),
    careProviderOrganizationId: Koi.string().uuid().allow(null),
    careProviderPractitionerId: Koi.string().uuid().allow(null),
    city: Koi.string().max(255).allow(null),
    country: Koi.string().max(255).allow(null),
    deceasedDate: Koi.string().max(50).allow(null),
    emailAddress: emailSchema.allow(null),
    gender: Koi.koi().enum(Gender),
    homePhoneNumber: Koi.string().max(255).allow(null),
    houseLetter: Koi.string().max(1).allow(null),
    houseNumber: Koi.string().max(255).allow(null),
    houseNumberSuffix: Koi.string().max(255).allow(null),
    identifierSocialSecurity: Koi.string().elfproef().allow(null),
    isDeceased: Koi.boolean(),
    maritalStatus: Koi.koi().enum(MaritalStatus).allow(null),
    mobileNumber: Koi.string().max(255).allow(null),
    nameFamily: Koi.string().max(255),
    nameFamily2: Koi.string().max(255).allow(null),
    nameGiven: Koi.string().max(255).allow(null),
    nameInfix: Koi.string().max(255).allow(null),
    nameInfix2: Koi.string().max(255).allow(null),
    nameUsual: Koi.string().max(255).allow(null),
    profession: Koi.string().max(255).allow(null),
    state: Koi.string().max(255).allow(null),
    street: Koi.string().max(255).allow(null),
    zip: Koi.string().max(255).allow(null),
});

const patientAddEdit = patientBase.keys({
    homePhoneNumber: phoneNumberNoErrorSchema.allow(null),
    houseNumber: Koi.when('street', {
        is: null,
        otherwise: Koi.numberAsString().disallow(null),
        then: Koi.when('zip', {
            is: null,
            otherwise: Koi.numberAsString().disallow(null),
            then: Koi.numberAsString().allow(null),
        }),
    }),
    mobileNumber: phoneNumberNoErrorSchema.allow(null),
    zip: Koi.string().allow(null).when('country', { is: 'NL', then: Koi.string().zipCodeNL() }),
});

export const newPatientSearchBarUpdateSchema: ISchemaContainer<IPatientAddEditValidated> = {
    schema: patientBase.options({ convert: false, presence: 'required' }),
};

export const patientSearchStartBarUpdateSchema: ISchemaContainer<IPatientAddEditValidated> = {
    schema: patientBase.options({ convert: false, presence: 'required' }),
};

export const patientAddEditSchema: ISchemaContainer<IPatientAddEditValidated> = {
    schema: patientAddEdit.options({ convert: false, presence: 'required' }),
};

export const patientUpdateSchema: ISchemaContainer<Partial<IPatientAddEditValidated>> = {
    schema: patientBase.options({ convert: false, presence: 'optional' }),
};

export const patientSsoUpdateSchema: ISchemaContainer<Partial<IPatientAddEditValidated>> = {
    schema: patientBase.options({ convert: false, presence: 'optional' }),
};

interface IPatientSearchBarValidated {
    birthDate: string | null;
    identifierSocialSecurity: string | null;
    processNumber: string | null;
}

export const newPatientSearchBarSchema: ISchemaContainer<IPatientSearchBarValidated> = {
    schema: Koi.object({
        birthDate: Koi.koi().date().required(),
        identifierSocialSecurity: Koi.string().elfproef().required(),
        processNumber: Koi.string().numeric().max(9).allow(null),
    }).options({ convert: false, presence: 'required' }),
};

export const patientSearchStartBarSchema: ISchemaContainer<IPatientSearchBarValidated> = {
    schema: Koi.object({
        birthDate: Koi.koi().date().allow(null),
        identifierSocialSecurity: Koi.string().elfproef().allow(null),
        processNumber: Koi.string().numeric().max(9).allow(null),
    }).options({ convert: false, presence: 'required' }),
};

export const patientsSearchSchema: ISchemaContainer<IPatientSearchBarValidated> = {
    schema: Koi.object({
        birthDate: Koi.koi().date().required(),
        identifierSocialSecurity: Koi.string().elfproef().required(),
    }).options({ convert: false, presence: 'required' }),
};

export interface IPatientGetDuplicatesValidated {
    birthDate: string | null;
    gender: Gender | null;
    id: string | null;
    identifierSocialSecurity: string | null;
    nameFamily: string | null;
    nameGiven: string | null;
    nameInfix: string | null;
}

export const patientGetDuplicatesSchema: ISchemaContainer<IPatientGetDuplicatesValidated> = {
    schema: Koi.object({
        birthDate: Koi.koi().date().allow(null),
        gender: Koi.koi().enum(Gender).allow(null),
        id: Koi.string().uuid().allow(null),
        identifierSocialSecurity: Koi.string().elfproef().allow(null),
        nameFamily: Koi.string().max(255).allow(null),
        nameGiven: Koi.string().max(255).allow(null),
        nameInfix: Koi.string().max(255).allow(null),
    }).options({ convert: false, presence: 'optional', stripUnknown: true }),
};

export interface IPatientSsoNotFoundValidated {
    birthDate: string;
    gender: Gender;
    identifierSocialSecurity: string;
    insurancePolicyNumber?: string;
    nameFamily: string;
    nameInfix?: string;
}

export const patientSsoNotFoundSchema: ISchemaContainer<IPatientSsoNotFoundValidated> = {
    schema: Koi.object({
        birthDate: Koi.koi().date(),
        gender: Koi.koi().enum(Gender),
        identifierSocialSecurity: Koi.string().elfproef(),
        insurancePolicyNumber: Koi.string().allow(null, '').optional(),
        nameFamily: Koi.string().max(255),
        nameInfix: Koi.string().max(255).allow(null, '').optional(),
    }).options({ convert: false, presence: 'required' }),
};

export interface IPatientCareProviderEditValidated {
    careProviderOrganizationId: string | null | undefined;
    careProviderPractitionerId: string | null | undefined;
}

const patientCareProviderEdit = Koi.object({
    careProviderOrganizationId: Koi.string().uuid().allow(null),
    careProviderPractitionerId: Koi.string().uuid().allow(null),
});

export const patientCareProviderEditSchema: ISchemaContainer<IPatientCareProviderEditValidated> = {
    schema: patientCareProviderEdit.options({ convert: false, presence: 'optional' }),
};

export const patientEmailOnlySchema: ISchemaContainer<IPatientAddEditValidated> = {
    schema: Koi.object({
        emailAddress: emailSchema.allow(null),
    }).options({ convert: false, presence: 'required' }),
};

export interface IPatientSearchWithBirthDateAndOtherOptions {
    birthDate: string;
    houseNumber: string;
    identifierSocialSecurity: string;
    insurancePolicyNumber: string;
    searchOption: PatientSearchOptionWithBirthDate;
    zip: string;
}

export interface IPatientSearchWithBirthDateAndOtherOptionsAndExtraQueryParameters
    extends IPatientSearchWithBirthDateAndOtherOptions {
    extraQueryParameters: {
        forwardToEpisode?: EpisodeCategory;
        newProcessCustomIdentifier?: string;
        serviceId?: string;
        ssoId?: string;
        withProcedure?: string;
    };
}

// eslint-disable-next-line @stylistic/max-len
export const patientSearchWithBirthDateAndOtherOptionsSchema: ISchemaContainer<IPatientSearchWithBirthDateAndOtherOptionsAndExtraQueryParameters> =
    {
        schema: Koi.object({
            birthDate: Koi.koi().date().required(),
            extraQueryParameters: Koi.object({
                forwardToEpisode: Koi.koi().enum(EpisodeCategory).optional(),
                newProcessCustomIdentifier: Koi.string().optional(),
                serviceId: Koi.string().uuid().optional(),
                ssoId: Koi.string().uuid().optional(),
                withProcedure: Koi.string().uuid().allow('true', 'false').optional(),
            }),
            houseNumber: Koi.string()
                .empty()
                .when('searchOption', {
                    is: PatientSearchOptionWithBirthDate.address,
                    otherwise: Koi.string().empty().allow(''),
                    then: Koi.string().max(255).required(),
                }),
            identifierSocialSecurity: Koi.when('searchOption', {
                is: PatientSearchOptionWithBirthDate.identifierSocialSecurity,
                otherwise: Koi.string().empty().allow(''),
                then: Koi.string().elfproef().required(),
            }),
            insurancePolicyNumber: Koi.when('searchOption', {
                is: PatientSearchOptionWithBirthDate.insurance,
                otherwise: Koi.string().empty().allow(''),
                then: Koi.string().max(255).required(),
            }),
            searchOption: Koi.koi().enum(PatientSearchOptionWithBirthDate),
            zip: Koi.when('searchOption', {
                is: PatientSearchOptionWithBirthDate.address,
                otherwise: Koi.string().empty().allow(''),
                then: Koi.string().max(255).required(),
            }),
        }),
    };
